/**
 * Page registry — the single source of truth for routing.
 *
 * Replaces `routes/cached.php` plus `resources/lang/{et,ru}/routes.php`. Each
 * page has a stable `key` (the old Laravel route name) and a per-locale slug.
 * Locales with no slug of their own fall back to the Estonian one, exactly as
 * `LaravelLocalization::transRoute()` did via the `et` fallback locale.
 */

import type { Component } from 'vue';
import { DEFAULT_LOCALE, type DefaultLocale, type Locale } from '@/i18n/locales';

/**
 * URL slug per locale. The default locale's slug is required and acts as the
 * fallback for every other locale, mirroring Laravel's `fallback_locale`.
 * An empty string means the locale root (the home page).
 */
export type LocalizedSlugs = Record<DefaultLocale, string> &
  Partial<Record<Locale, string>>;

export interface PageDefinition {
  /** Stable identifier, also the vue-router name stem. */
  key: string;
  slugs: LocalizedSlugs;
  /** Lazily loaded page component. */
  component: () => Promise<Component>;
}

export const PAGES: PageDefinition[] = [
  {
    key: 'home',
    slugs: { et: '' },
    component: () => import('@/views/HomeView.vue'),
  },
  {
    key: 'about',
    slugs: { et: 'geenivaramu' },
    component: () => import('@/views/AboutView.vue'),
  },
  {
    key: 'medications',
    slugs: { et: 'ravimid', ru: 'лекарства' },
    component: () => import('@/views/MedicationsView.vue'),
  },
  {
    key: 'mental-health-2021',
    slugs: { et: 'vaimnetervis-2021' },
    component: () => import('@/views/MentalHealth2021View.vue'),
  },
  {
    key: 'mental-health-2026',
    slugs: { et: 'vaimnetervis' },
    component: () => import('@/views/MentalHealth2026View.vue'),
  },
  {
    key: 'contact',
    slugs: { et: 'kontakt' },
    component: () => import('@/views/ContactView.vue'),
  },
  {
    key: 'terms',
    slugs: { et: 'nousoleku-vorm' },
    component: () => import('@/views/TermsView.vue'),
  },
  {
    key: 'info',
    slugs: { et: 'infoleht', ru: 'информационный-лист' },
    component: () => import('@/views/InfoView.vue'),
  },
  {
    key: 'for-donor',
    slugs: { et: 'geenidoonorile' },
    component: () => import('@/views/ForDonorView.vue'),
  },
  {
    key: 'privacy-and-cookie-policy',
    slugs: { et: 'privaatsuspoliitika-ja-küpsised' },
    component: () => import('@/views/PrivacyAndCookiePolicyView.vue'),
  },
  {
    key: 'personality-research',
    slugs: { et: 'isiksuseuuring' },
    component: () => import('@/views/PersonalityResearchView.vue'),
  },
  {
    key: 'microbiome',
    slugs: { et: 'mikrobioom' },
    component: () => import('@/views/MicrobiomeView.vue'),
  },
  {
    key: 'diabetes-study',
    slugs: { et: 'diabeediuuring' },
    component: () => import('@/views/DiabetesStudyView.vue'),
  },
  {
    key: 'sudameuuring',
    slugs: { et: 'sudameuuring' },
    component: () => import('@/views/SudameuuringView.vue'),
  },
];

/**
 * Retired pages that must keep redirecting to the home page.
 *
 * Donor intake and the eID login/signing flow were switched off in the Laravel
 * app (`Route::redirect(..., '/')` in routes/cached.php and routes/web.php).
 * The slugs still receive traffic and inbound links, so the redirects are
 * carried over rather than dropped.
 */
export const RETIRED_SLUGS: LocalizedSlugs[] = [
  { et: 'hakka-doonoriks' }, // becomedonor
  { et: 'nousolek' }, // informed-consent
  { et: 'hakka-geenidoonoriks', ru: 'стать-генным-донором' }, // time-pressure
  { et: 'verevotukohad' }, // locations
  { et: 'registreeru', ru: 'зарегистрироваться' }, // auth — login removed
  { et: 'allkirjasta', ru: 'подписать' }, // sign — signing removed
  { et: 'aitah', ru: 'форма-согласия-подписана' }, // thanks — signing removed
  { et: 'kkk' }, // faq — never had a route, kept for old links
];

/** Slug for a page in a locale, falling back to Estonian. */
export function slugFor(page: PageDefinition, locale: Locale): string {
  return page.slugs[locale] ?? page.slugs[DEFAULT_LOCALE];
}

/** vue-router route name for a page in a locale, e.g. `contact__ru`. */
export function routeName(key: string, locale: Locale): string {
  return `${key}__${locale}`;
}
